<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import CopyIcon from '@/components/icons/CopyIcon.vue'
import { levelNames, useConfig } from '@/composables/game/config'
import { useReferredFriends } from '@/composables/game/referred-friends'
import { useReferral } from '@/composables/referral'
import type { Level } from '@/types/level'
import { useClipboard } from '@/composables/helpers/clipboard'
import type { Friend } from '@/types/referral'
import { buildDropeeStorageKey } from '@/config/config'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { getLevelAssetUrl } from '@/helpers/game'
import webapp from '@/services/webapp'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { useRoute } from 'vue-router'
import { T, useTranslate } from '@tolgee/vue'
import { useLocalStorage } from '@vueuse/core'
import { DateTime } from 'luxon'
import { useAnalytics } from '@/composables/analytics'
import { usePlayer } from '@/composables/game/player'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import ChallengeRockPaperScissorsModal from '@/components/modals/ChallengeRockPaperScissorsModal.vue'
import type { Nullable } from '@/types/shared/common'
import { showHiddenFeatures } from '@/composables/feature-flag'
import FriendCard from '@/components/FriendCard.vue'
import RefreshIcon from '@/components/icons/RefreshIcon.vue'
import ThePremium from '@/components/ThePremium.vue'
import Emoji1 from '@/assets/images/friends-emojis/1.png'
import Emoji2 from '@/assets/images/friends-emojis/2.png'
import Emoji3 from '@/assets/images/friends-emojis/3.png'
import Emoji4 from '@/assets/images/friends-emojis/4.png'
import Emoji5 from '@/assets/images/friends-emojis/5.png'
import Emoji6 from '@/assets/images/friends-emojis/6.png'
import Emoji7 from '@/assets/images/friends-emojis/7.png'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import FriendsInfoModal from '@/components/modals/FriendsInfoModal.vue'
import InviteFriendPrizeBox from '@/components/InviteFriendPrizeBox.vue'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { useGiveaway } from '@/composables/game/giveaway'
import BaseInfiniteScroll from '@/components/base/BaseInfiniteScroll.vue'

const { config } = useConfig()
const { notify } = useToaster()
const {
  getFriends,
  playerFriends,
  playerPokes,
  referralsCount,
  friendsCount,
  virtualFriendIdPrefix,
  pokeMemory,
  pokeLimit,
} = useReferredFriends()
const { shareUrl, referralUrl } = useReferral()
const { updatePlayer, player } = usePlayer()
const route = useRoute()
const { t } = useTranslate()
const { addHapticFeedback } = useHapticFeedback()
const { canShowOnboardingEmoji, showOnboardingEmojiFriends } = useOnboardingFingerEmojis()
const friendshipIdFilter = ref(route.query.friendshipId as string)

const { copyToClipboard } = useClipboard()
const { triggerEvent } = useAnalytics()
const { countdown: giveawayCountdown } = useGiveaway()

const friends = computed<Friend[]>(() => playerFriends.value)
const pokedFriendIds = computed<string[]>(() => playerPokes.value)
const friendsToShow = computed(() => friendshipIdFilter.value ? friends.value.filter((f) => f.id === friendshipIdFilter.value) : friends.value)

const virtualCount = computed(() => player.value.friends.virtualCount)

// Combined list of all friends (real + virtual) for infinite scroll
const allFriendsList = computed<Friend[]>(() => {
  const realFriends = friendsToShow.value
  const virtualFriends = Array.from({ length: virtualCount.value }, (_, index) => getVirtualFriend(index + 1))
  return [...realFriends, ...virtualFriends]
})

const showMore = ref(false)
const isFetching = ref(false)
const startButtonScaleUp = ref(false)
const pokedFriendId = ref<null | string>(null)

const showFriendsInfoModal = ref<boolean>(false)

const totalAmountToShow = computed(() => friends.value.reduce((acc, friend) => {
  if (friend.referralGain) {
    return acc + friend.referralGain
  }
  return acc
}, 0))

const levels = computed<Level[]>(() => {
  const list = config.value.game.levels
  return list.slice().reverse()
})

const lastRefresh = useLocalStorage<string | null>(buildDropeeStorageKey('last_friends_refresh'), null)

const emojiList = [Emoji1, Emoji2, Emoji3, Emoji3, Emoji4, Emoji5, Emoji6, Emoji7]

const rpsChallengedFriend = ref<Nullable<Friend>>(null)

const fullScreenStyles = computed(() => {
  return { bottom: `${webapp.safeAreaInset.bottom + 70}px` }
})

function getReferralLevel(level: number) {
  return config.value.game.referrals.perLevelReferrerReward.find((l) => l.level === level) ?? config.value.game.referrals.perLevelReferrerReward[0]
}

function copyInviteLink() {
  copyToClipboard(referralUrl.value, t.value('general.link_copied', 'Link copied!'))
  triggerEvent({ event: 'share' })
}

async function pokeFriend(friend: Friend) {
  if (pokedFriendId.value) return
  pokedFriendId.value = friend.id
  try {
    const { success, activities } = await api.pokeFriend(friend.id)
    if (success) {
      playerPokes.value.push(friend.id)
      triggerEvent({ event: 'poke' })
      notify({
        title: t.value('friends.friend_poked', '{name} has been poked!', { name: friend.firstName }),
        type: 'success',
      })

      const currentCount = pokeMemory.value.count + 1
      if (currentCount >= pokeLimit.value) {
        pokeMemory.value.timestamp = DateTime.now().toJSDate().toISOString()
        pokeMemory.value.count = 0
      } else {
        pokeMemory.value.count += 1

      }

      updatePlayer({ ...player.value, activities })
    }
  } finally {
    friendshipIdFilter.value = ''
    pokedFriendId.value = null
  }
}

function refreshFriendsList() {
  friendshipIdFilter.value = ''
  if (lastRefresh.value) {
    const elapsedTime = DateTime.now().diff(DateTime.fromISO(lastRefresh.value), 'seconds').seconds
    if (!!lastRefresh.value && elapsedTime < 60) {
      notify({
        title: t.value('friends.wait_refresh', 'Wait {time} seconds to refresh', { time: Math.round(60 - elapsedTime) }),
        type: 'warn',
      })
      return
    }
  }
  isFetching.value = true
  lastRefresh.value = DateTime.now().toJSDate().toISOString()
  setTimeout(async () => {
    await getFriends(true)
    isFetching.value = false
  }, 500)

}

function getVirtualFriend(index: number): Friend {
  return {
    id: `${virtualFriendIdPrefix.value}${index}`,
    referralGain: null,
    firstName: `Dropeer#${index}`,
    profit: 0,
    experience: 0,
    photoUrl: null,
    level: 0,
    isPremium: false,
    isVirtual: true,
  }
}

function openTelegramShare() {
  showOnboardingEmojiFriends.value = false
  addHapticFeedback()
  triggerEvent({ event: 'share' })
  webapp.openTelegramLink(shareUrl.value)
}

async function challengeFriend(friend: Friend) {
  if (!showHiddenFeatures.value) return
  rpsChallengedFriend.value = friend
}

async function submitRockPaperScissorsChallenge(moves: string[]) {
  if (!rpsChallengedFriend.value) return
  const friendshipId = rpsChallengedFriend.value.id
  rpsChallengedFriend.value = null
}

onMounted(async () => {
  setTimeout(() => startButtonScaleUp.value = true, 400)
})

onBeforeMount(async () => {
  isFetching.value = true
  await getFriends()
  isFetching.value = false
})

</script>
<template>
  <div class="px-4 flex flex-col h-full py-6 z-[1]">
    <div class="flex flex-col items-center mb-10">
      <img
        src="@/assets/images/gifs/duck-family.gif"
        class=" w-[120px] h-[120px] min-w-[120px] mb-6 bounce-down-fast"
      >
      <h1 class="bounce-in-right font-bold text-[32px] leading-8 text-light-white uppercase">
        <T
          key-name="friends.title"
          default-value="Invite friends"
        />
      </h1>
      <p class="bounce-in-left font-medium text-base text-light-white/70 text-center">
        <T
          key-name="friends.subtitle2"
          default-value="Earn rewards by inviting new friends (referral) to join the game or add existing friends!"
        />
      </p>
    </div>

    <div
      class="py-6 w-full rounded-xl border-2 border-[#373739] flex flex-col justify-center items-center gap-y-3 bg-dark-primary"
      :class="friendsToShow.length ? 'mb-10' : 'mb-4'"
    >
      <p class="text-white text-base font-semibold tracking-tight">
        <T
          key-name="friends.total_received"
          default-value="Received from referred friends"
        />
      </p>
      <NumberFormatTooltip
        :number="totalAmountToShow"
        :formatting-type="'full'"
      >
        <div class="flex gap-x-2 items-center">
          <CoinIcon class="text-white w-5" />
          <span class="text-white font-bold text-[32px] leading-10">{{
            formatNumberFullTruncate(totalAmountToShow)
          }}</span>
        </div>
      </NumberFormatTooltip>
    </div>

    <div class="flex flex-col h-full">
      <div
        class="flex"
        :class="friendsToShow.length ? 'flex-col' : 'flex-col-reverse'"
      >
        <div class="flex flex-col w-full gap-y-3 mb-10">
          <div class="flex w-full flex-col gap-y-1">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-x-2">
                <p class="text-base font-semibold text-light-white/70 tracking-tight">
                  <span class="mr-1 text-base text-white font-medium">{{ friendsCount + virtualCount }}</span>
                  <T
                    key-name="friends.list.title"
                    default-value="friends"
                  />
                </p>
                <RefreshIcon
                  class="cursor-pointer"
                  :class="{'rotate-right': isFetching}"
                  @click="refreshFriendsList"
                />
              </div>
              <InfoCircleIcon
                class="text-white/[38%] size-6"
                @click="showFriendsInfoModal = true"
              />
            </div>
            <p
              v-if="referralsCount"
              class="text-sm text-white/60 font-medium -tracking-[0.28px]"
            >
              <T
                key-name="friends.referrals_count"
                default-value="{count, plural, one {# referral} other {# referrals}}"
                :params="{count: referralsCount}"
              />
            </p>
          </div>

          <div
            v-if="!isFetching"
            class="flex flex-col w-full min-h-[52px]"
          >
            <BaseInfiniteScroll
              v-if="allFriendsList.length > 0"
              class="flex flex-col gap-y-3"
              :items="allFriendsList"
              :items-per-page="20"
              :load-more-threshold="300"
            >
              <template #item="{ item: friend }">
                <FriendCard
                  :friend="friend"
                  :poked="pokedFriendIds.includes(friend.id)"
                  :is-poking="pokedFriendId === friend.id"
                  @on-challenge="challengeFriend(friend)"
                  @on-poke="pokeFriend(friend)"
                />
              </template>
            </BaseInfiniteScroll>
            <div
              v-else
              class="p-3 rounded-xl bg-dark-bottom-layer flex flex-col items-center text-center"
            >
              <div class="flex justify-between items-center max-w-[312px] w-full mb-3">
                <img
                  v-for="i in 7"
                  :key="i"
                  :src="emojiList[i]"
                  class="size-6"
                >
              </div>
              <p class="text-base font-semibold text-white tracking-tight mb-0.5">
                <T
                  key-name="friends.list.empty.title"
                  default-value="Get your first friend!"
                />
              </p>
              <p class="text-sm font-semibold -tracking-[0.28px] text-white/60">
                <T
                  key-name="friends.list.empty.description"
                  default-value="Together you will get the highest rewards and profits!"
                />
              </p>
            </div>
          </div>
          <div
            v-else
            class="flex space-x-1.5 justify-center items-center h-[52px]"
          >
            <span class="loading-dot rounded-full animate-bounce ![animation-delay:-0.25s]" />
            <span class="loading-dot rounded-full animate-bounce ![animation-delay:-0.1s]" />
            <span class="loading-dot rounded-full animate-bounce" />
          </div>
        </div>

        <div class="flex flex-col gap-y-6">
          <div
            class="gap-3 grid grid-cols-[1fr_1fr]"
          >
            <InviteFriendPrizeBox
              :title="t('friends.refer_new_friend', 'Refer a new friend to join Dropee')"
              :amount="config.game.referrals.initialRefereeReward.normal"
              :spins="giveawayCountdown ? 0 : config.game.fortuneWheel.extraSpin.referral.onboarding"
              :tickets="config.game.referrals.giveawayTickets"
            />
            <InviteFriendPrizeBox
              :amount="config.game.referrals.initialRefereeReward.premium"
              :spins="giveawayCountdown ? 0 : config.game.fortuneWheel.extraSpin.referral.onboarding"
              :tickets="config.game.referrals.giveawayTickets"
            >
              <template #title>
                <p class="font-semibold text-base tracking-tight max-[420px]:text-sm text-center whitespace-pre-line">
                  <T
                    key-name="friends.refer_with_premium"
                    :default-value="`Refer a new friend with \n Telegram Premium`"
                  />
                </p>
              </template>
            </InviteFriendPrizeBox>
          </div>
          <div class="flex flex-col items-center mb-6">
            <BaseLinkButton
              v-if="!showMore"
              @click="() => showMore = true"
              class=""
              button-class="py-1"
            >
              <T
                key-name="friends.level_up.show_more"
                default-value="More Bonuses"
              />
            </BaseLinkButton>
            <div
              v-if="showMore"
              class="flex flex-col w-full gap-y-1 overflow-x-hidden"
            >
              <p
                class="text-2xl font-semibold"
                @click="() => showMore = false"
              >
                <T
                  key-name="friends.level_up.title"
                  default-value="Bonus for levelling up"
                />
              </p>
              <table class="w-full border-spacing-y-2 border-separate">
                <tbody>
                  <tr class="text-base w-full font-semibold text-light-white/70 tracking-tight mb-1">
                    <td class="text-left">
                      <T
                        key-name="friends.list.column.level"
                        default-value="Level"
                      />
                    </td>
                    <td class="text-left pr-2">
                      <T
                        key-name="friends.list.column.for_friend"
                        default-value="For friend"
                      />
                    </td>
                    <td class="text-left pr-3.5 max-[420px]:pr-2">
                      <div class="flex items-center gap-x-0.5">
                        <T
                          key-name="friends.list.column.premium"
                          default-value="Premium"
                        />
                        <ThePremium />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-for="level in levels"
                    :key="level.level"
                    class="bg-dark-bottom-layer w-full rounded-xl justify-between items-center truncate"
                  >
                    <td class="truncate w-full max-w-0 pr-2">
                      <div class=" flex gap-x-2 items-center pl-2 max-[420px]:pl-1 py-2 truncate w-full">
                        <div
                          class="relative size-[42px] min-w-[42px] flex justify-center items-center"
                        >
                          <img
                            :src="getLevelAssetUrl(level.level)"
                            alt="Level image"
                            class="h-[48px] w-[48px] min-w-[48px] absolute object-cover -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2"
                          >
                        </div>
                        <p
                          v-tooltip="{content: levelNames[level.level]}"
                          class="text-base font-semibold tracking-tight text-wrap max-[420px]:text-sm"
                        >
                          <T
                            key-name="levels.level"
                            default-value="Level {index}"
                            :params="{
                              index: level.level
                            }"
                          />
                        </p>
                      </div>
                    </td>
                    <td class="whitespace-nowrap pr-2">
                      <div class="flex items-center overflow-hidden justify-start">
                        <NumberFormatTooltip
                          :number="getReferralLevel(level.level).normal"
                          :formatting-type="'full'"
                        >
                          <div
                            class="flex gap-x-1 justify-center items-center rounded-3xl px-1.5 py-0.5 green-gradient-chip w-max overflow-hidden"
                          >
                            <CoinIcon class="h-5 w-3" />
                            <span class="text-base text-solid-white font-semibold truncate max-[420px]:text-sm">+{{
                              formatNumberFullTruncate(getReferralLevel(level.level).normal)
                            }}</span>
                          </div>
                        </NumberFormatTooltip>
                      </div>
                    </td>
                    <td class="whitespace-nowrap">
                      <div class="flex items-center overflow-hidden pr-3.5 justify-start">
                        <NumberFormatTooltip
                          :number="getReferralLevel(level.level).premium"
                          :formatting-type="'full'"
                        >
                          <div
                            class="flex gap-x-1 justify-center items-center rounded-3xl px-1.5 py-0.5 green-gradient-chip w-max overflow-hidden"
                          >
                            <CoinIcon class="h-5 w-3" />
                            <span class="text-base text-solid-white font-semibold truncate max-[420px]:text-sm">+{{
                              formatNumberFullTruncate(getReferralLevel(level.level).premium)
                            }}</span>
                          </div>
                        </NumberFormatTooltip>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex w-full gap-x-3 sticky h-full grow-up-from-bottom"
      :style="fullScreenStyles"
    >
      <img
        v-if="canShowOnboardingEmoji && showOnboardingEmojiFriends"
        class="pointer-events-none absolute -top-[48px] left-1/2 translate-x-[calc(-50%+60px)]  size-[88px] rotate-[-145deg] object-cover z-[1]"
        :src="FingerUpGif"
      >
      <BaseButton
        class="w-full"
        :class="{'scale-up': startButtonScaleUp}"
        size-type="small"
        @click="openTelegramShare"
      >
        <T
          key-name="friends.cta"
          default-value="Invite a friend"
        />
      </BaseButton>
      <BaseButton
        size-type="small"
        button-class="!aspect-square"
        @click="copyInviteLink()"
      >
        <CopyIcon class="stroke-2" />
      </BaseButton>
    </div>
  </div>
  <ChallengeRockPaperScissorsModal
    v-if="Boolean(rpsChallengedFriend)"
    :show="Boolean(rpsChallengedFriend)"
    :friend-first-name="rpsChallengedFriend?.firstName ?? ''"
    @hide="rpsChallengedFriend = null"
    @submit="submitRockPaperScissorsChallenge"
  />

  <FriendsInfoModal
    v-if="showFriendsInfoModal"
    :show="showFriendsInfoModal"
    centered
    @hide="showFriendsInfoModal = false"
  />
</template>
<style scoped lang="scss">
.circle-ring {
	background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.2));
}

.gray-linear {
	background: linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.00) 100%)
}

.orange-linear {
	background: linear-gradient(180deg, rgba(253, 176, 55, 0.32) 0%, rgba(253, 176, 55, 0.00) 100%)
}

@keyframes rotate-right {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

@keyframes bounce {
	0%, 100% {
		transform: translateY(-35%);
		animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
	}
	50% {
		transform: translateY(0);
		animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}
}

.rotate-right {
	animation: rotate-right 2s linear infinite;
}

.animate-bounce {
	animation: bounce 0.75s infinite;
}

.loading-dot {
	@apply h-1.5 w-1.5 text-white bg-white;
}

td:first-child,
th:first-child {
	border-radius: 16px 0 0 16px;
}

td:last-child,
th:last-child {
	border-radius: 0 16px 16px 0;
}

.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.poke-text-shadow {
	text-shadow: 0px 0px 56px rgba(0, 0, 0, 0.50)
}

.green-gradient-chip {
	background: linear-gradient(92deg, rgba(126, 187, 0, 0.75) -4.79%, rgba(187, 238, 83, 0.75) 23.5%);
}
</style>
